
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatCurrency } from '@/lib/utils';
import { Edit, Eye, MoreHorizontal, Trash2 } from 'lucide-react';
import { Skeleton } from "@/components/ui/skeleton";
import { DeleteConfirmation } from "@/components/ui/delete-confirmation";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface InventoryItem {
  id: string;
  gift_name: string;
  description: string | null;
  category: string | null;
  cost: number | null;
  gift_tier: string | null;
  quantity_available: number | null;
  inventory_status: string | null;
  image_url: string | null;
}

interface GiftInventoryListProps {
  inventory: InventoryItem[];
  isLoading: boolean;
  onInventoryChange?: () => void;
}

const InventoryStatusBadge = ({ status }: { status: string | null }) => {
  if (!status) return null;

  const statusMap: Record<string, { color: string, label: string }> = {
    'available': { color: 'green', label: 'Available' },
    'low_stock': { color: 'orange', label: 'Low Stock' },
    'out_of_stock': { color: 'destructive', label: 'Out of Stock' },
    'discontinued': { color: 'secondary', label: 'Discontinued' },
  };

  const statusConfig = statusMap[status] || { color: 'default', label: status };

  return (
    <Badge variant={statusConfig.color as any}>{statusConfig.label}</Badge>
  );
};

const GiftTierBadge = ({ tier }: { tier: string | null }) => {
  if (!tier) return null;

  const tierMap: Record<string, { color: string, label: string }> = {
    'standard': { color: 'default', label: 'Standard' },
    'premium': { color: 'blue', label: 'Premium' },
    'luxury': { color: 'purple', label: 'Luxury' },
    'executive': { color: 'orange', label: 'Executive' },
  };

  const tierConfig = tierMap[tier] || { color: 'default', label: tier };

  return (
    <Badge variant={tierConfig.color as any}>{tierConfig.label}</Badge>
  );
};

const GiftInventoryList: React.FC<GiftInventoryListProps> = ({ inventory, isLoading, onInventoryChange }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = (item: InventoryItem) => {
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedItem) return;

    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('gift_inventory')
        .delete()
        .eq('id', selectedItem.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: `${selectedItem.gift_name} has been deleted from inventory.`,
      });

      onInventoryChange?.();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete inventory item",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setSelectedItem(null);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center space-x-4 p-2">
            <Skeleton className="h-6 w-1/4" />
            <Skeleton className="h-6 w-1/4" />
            <Skeleton className="h-6 w-1/4" />
            <Skeleton className="h-6 w-1/4" />
          </div>
        ))}
      </div>
    );
  }

  if (inventory.length === 0) {
    return (
      <div className="text-center py-10">
        <h3 className="text-lg font-medium">No inventory items yet</h3>
        <p className="text-muted-foreground">Add items to your gift inventory to get started.</p>
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Tier</TableHead>
            <TableHead>Cost</TableHead>
            <TableHead>Quantity</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="w-[80px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {inventory.map((item) => (
            <TableRow key={item.id}>
              <TableCell className="font-medium">{item.gift_name}</TableCell>
              <TableCell>{item.category || '-'}</TableCell>
              <TableCell>
                <GiftTierBadge tier={item.gift_tier} />
              </TableCell>
              <TableCell>{item.cost ? formatCurrency(item.cost) : '-'}</TableCell>
              <TableCell>{item.quantity_available ?? 0}</TableCell>
              <TableCell>
                <InventoryStatusBadge status={item.inventory_status} />
              </TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="h-8 w-8 p-0">
                      <span className="sr-only">Open menu</span>
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>Actions</DropdownMenuLabel>
                    <DropdownMenuItem onClick={() => navigate(`/gifts/inventory/${item.id}`)}>
                      <Eye className="mr-2 h-4 w-4" />
                      View
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate(`/gifts/inventory/${item.id}/edit`)}>
                      <Edit className="mr-2 h-4 w-4" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      className="text-destructive"
                      onClick={() => handleDeleteClick(item)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      
      <DeleteConfirmation
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteConfirm}
        title="Delete Inventory Item"
        description={
          selectedItem
            ? `Are you sure you want to delete "${selectedItem.gift_name}" from your gift inventory? This action cannot be undone.`
            : ""
        }
        isLoading={isDeleting}
      />
    </div>
  );
};

export default GiftInventoryList;
